import { useCallback, useEffect, useState } from 'react';
import { useGetApiData } from '@grc/ui-utility';
import { useI18nState } from '../../../stores/settings/language-context';
import { useHomeStore } from '../../../stores/home-store';
import { operationalViewCardsConfig } from './operational-view-cards.config';
import {
  IOperationalCardCounts,
  IOperationalCardData
} from '../../../models/home/operational-card.interface';
import I18N from '../../../i18n/interface';
import { GetMyWorkItemsSummary } from '../../../service/api/get-my-work-items-summary-service';
import { GetQuoteWorkItemsSummary } from '../../../service/api/get-quote-work-items-summary-service';
import { homeShipmentsAccountId } from '../../../common/constants/constants';
import { toApiDateRange } from '../../../common/date-range-filter/date-range-filter.utils';

type CardCounts = Record<string, Record<string, number>>;

// Adapts MyWorkItemsSummary's response (cards keyed by backend CardType, each with its own
// Counts dictionary) onto the CardCounts shape resolveCards() expects (keyed by frontend card/section
// key), using each section's workItem mapping. Sections without a workItem (weeklySchedules)
// are left out and simply render 0.
function mapWorkItemCardsToCounts(cards: IOperationalCardCounts[]): CardCounts {
  const counts: CardCounts = {};

  Object.entries(operationalViewCardsConfig).forEach(([cardKey, config]) => {
    counts[cardKey] = {};
    config.sections.forEach(section => {
      const { workItem } = section;
      if (!workItem) return;
      const backendCard = cards.find(card => card.cardType === workItem.cardType);
      counts[cardKey][section.key] = backendCard?.counts[workItem.countsKey] ?? 0;
    });
  });

  return counts;
}

function resolveCards(
  counts: CardCounts,
  localeData: I18N
): IOperationalCardData[] {
  return Object.entries(operationalViewCardsConfig).map(
    ([cardType, config]) => ({
      cardType,
      title: localeData[config.titleKey] as string,
      icon: config.icon,
      routePath: config.routePath,
      breadcrumbLabel: localeData[config.breadcrumbLabelKey] as string,
      sections: config.sections.map((section, index) => ({
        key: section.key,
        label: localeData[section.labelKey] as string,
        value: counts[cardType]?.[section.key] ?? 0,
        isPrimary: index === 0,
        tabKey: section.tabKey
      }))
    })
  );
}

export function useOperationalViewSummary() {
  const { localeData } = useI18nState();
  const { fetchData } = useGetApiData();
  const [fromDate] = useHomeStore(store => store.fromDate);
  const [toDate] = useHomeStore(store => store.toDate);
  const [dateFilterType] = useHomeStore(store => store.dateFilterType);

  const [cards, setCards] = useState<IOperationalCardData[]>(() =>
    resolveCards({}, localeData)
  );
  const [isLoading, setIsLoading] = useState(true);

  const getSummaryData = useCallback(async (): Promise<CardCounts> => {
    const apiDateRange = toApiDateRange({ fromDate, toDate });

    // Shipments (Order.Service) and Pending Approvals (Quotes.Service) live behind separate
    // microservices with separate summary endpoints - fetched in parallel and merged into one
    // card list. A failure in either resolves to [] (both services catch internally), so the
    // other domain's cards still render rather than the whole Home page failing to load.
    const [orderCards, quoteCards] = await Promise.all([
      GetMyWorkItemsSummary(fetchData, {
        accountId: homeShipmentsAccountId,
        ...apiDateRange
      }),
      GetQuoteWorkItemsSummary(fetchData, {
        accountIds: [homeShipmentsAccountId],
        dateFilterType,
        ...apiDateRange
      })
    ]);
    console.log(orderCards,quoteCards,[])
    return mapWorkItemCardsToCounts([...orderCards, ...quoteCards]);
  }, [fetchData, fromDate, toDate, dateFilterType]);

  useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      setIsLoading(true);
      const counts = await getSummaryData();
      if (!isCancelled) {
        setCards(resolveCards(counts, localeData));
        setIsLoading(false);
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [getSummaryData, localeData]);

  return { cards, isLoading };
}

export default useOperationalViewSummary;
